import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="loan-application-form-1"
export default class extends Controller {

  static values = { presetAmount: Number, wantsInstantLoan: Boolean, presetFinanceCase: String, brokerForm: Boolean }
  static targets = ["financeCase", "deviceFinancingContainer", "deviceFinancingField", "instantLoanWantedField",
    "olderThanFiveYearsTrue", "olderThanFiveYearsFalse", "olderThanFiveYearsGroup", "notMyPracticeInput",
    "amount", "amountContainer", "duration", "durationContainer", "inputContainer", "submitButton"];

  connect() {
    this.setPresetFinanceCase();
    this.reevaluate();
  }

  reevaluate() {
    this.reevaluateDeviceFinancing();
    this.reevaluateOlderThanFiveYears();
    this.reevaluateAmount();
    this.reevaluateDuration();
    this.setInstantLoanWantedField();
    this.reevaluateShowInputs();
  }

  setPresetFinanceCase(){
    this.financeCaseTarget.querySelectorAll('option').forEach(option => {
      if (option.value === this.presetFinanceCaseValue) {
        option.selected = 'selected'
        this.financeCaseTarget.value = option.value
      }
    })
  }

  selectPractice() {
    this.notMyPracticeInputTarget.value = false;
    this.notMyPracticeInputTarget.checked = false;
    this.practiceSelected = true;
    this.reevaluate();
  }

  deselectPractice() {
    this.notMyPracticeInputTarget.value = true;
    this.notMyPracticeInputTarget.checked = true;
    this.practiceSelected = false;
    this.reevaluate();
  }

  reevaluateShowInputs() {
    if (this.brokerFormValue) {
      if (this.practiceSelected) {
        this.inputContainerTargets.forEach(input => input.classList.remove('is-hidden'));
        this.submitButtonTargets.forEach(button => button.classList.remove('is-hidden'));
      } else {
        this.inputContainerTargets.forEach(input => input.classList.add('is-hidden'));
        this.submitButtonTargets.forEach(button => button.classList.add('is-hidden'));
      }
    }
  }

  setInstantLoanWantedField() {
    const amount = this.amountTarget.value;
    const duration = this.durationTarget.value;
    const financeCase = this.financeCaseTarget.value;
    const deviceFinancingType = this.hasDeviceFinancingFieldTarget ? this.deviceFinancingFieldTarget.value : null;
    const instantLoanableFinanceCase = ['Equipment', 'Liquidity', 'Debt Restructuring'].includes(financeCase) && (deviceFinancingType === 'Loan' || deviceFinancingType === "Dont Know" || deviceFinancingType === null || deviceFinancingType === '' || typeof deviceFinancingType === 'undefined');
    const instantLoan = instantLoanableFinanceCase && amount > 0 && amount <= 100000 && duration > 0 && duration <= 60;
    if (instantLoan) {
      this.instantLoanWantedFieldTarget.value = true
    } else {
      this.instantLoanWantedFieldTarget.value = false
    }
  }

  reevaluateDeviceFinancing() {
    if (this.financeCaseTarget.value === 'Equipment') {
      if (this.hasDeviceFinancingContainerTarget) {
        this.deviceFinancingContainerTarget.classList.remove('is-hidden');
        this.deviceFinancingContainerTarget.classList.add('presence-validated');
      }
    } else {
      if (this.hasDeviceFinancingFieldTarget) {
        Array.from(this.deviceFinancingFieldTarget.children).forEach(element => {
          if (element.value === null) {
            element.selected = 'selected'
          } else {
            element.selected = null
          }

        })
      }
      if (this.hasDeviceFinancingContainerTarget) {
        this.deviceFinancingContainerTarget.classList.add('is-hidden');
        this.deviceFinancingContainerTarget.classList.remove('presence-validated');
      }
    }
  }

  reevaluateOlderThanFiveYears() {
    if (this.financeCaseTarget.value === 'Existing Practice') {
      this.olderThanFiveYearsGroupTarget.classList.remove('is-hidden');
    } else {
      this.olderThanFiveYearsTrueTarget.checked = false;
      this.olderThanFiveYearsFalseTarget.checked = true;
      this.olderThanFiveYearsGroupTarget.classList.add('is-hidden');
    }
    // Order is important, the click event must come first, since it triggers the value inside
    // the interest rate calculator to change, the change event triggers the calculation
    this.olderThanFiveYearsFalseTarget.dispatchEvent(new Event('click'))
    this.olderThanFiveYearsTrueTarget.dispatchEvent(new Event('change'))
    this.olderThanFiveYearsFalseTarget.dispatchEvent(new Event('change'))
  }

  reevaluateAmount() {
    const financeCase = this.financeCaseTarget.value;
    const selectedAmount = this.amountTarget.value !== '' ? this.amountTarget.value : this.presetAmountValue;  // Get the currently selected amount
    const amountOptions = this.calculateAmountOptions(financeCase);

    // Clear current options, but keep the default null option
    this.amountTarget.innerHTML = "";

    // Add the null option back
    const nullOption = document.createElement("option");
    nullOption.value = "";
    nullOption.text = "Bitte wählen";
    this.amountTarget.appendChild(nullOption);

    // Populate with new valid options
    amountOptions.forEach((option) => {
      const optionElement = document.createElement("option");
      optionElement.value = option;
      optionElement.text = `${option.toLocaleString('de-DE',
        {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })}`;

      // Check if the current selected amount is still valid
      if (parseInt(selectedAmount) === option) {
        optionElement.selected = true;  // Keep the user’s selection if still valid
      }

      this.amountTarget.appendChild(optionElement);
    });

    // If the selected amount is no longer valid, set it to the null option
    if (!amountOptions.includes(parseInt(selectedAmount))) {
      this.amountTarget.value = "";  // Revert to the null option
    }

    if (financeCase === "" || financeCase === null) {
      this.amountContainerTarget.classList.add("is-hidden")
    } else {
      this.amountContainerTarget.classList.remove("is-hidden")
    }
  }

  calculateAmountOptions(financeCase) {
    if (financeCase === "" || financeCase === 0) {
      return [];
    }

    let options = [];

    // Logic for recalculating amounts
    if (this.wantsInstantLoanValue) {
      options = [
        ...this.range(20_000, 50_000, 1_000),
        ...this.range(50_000, 102_500, 2_500)
      ];
    } else {
      if (financeCase === "New Practice" || financeCase === "Buy Practice") {
        options = [
          ...this.range(50_000, 100_000, 5_000),
          ...this.range(100_000, 500_000, 10_000),
          ...this.range(500_000, 2_000_000, 25_000),
          ...this.range(2_000_000, 5_000_000, 100_000),
        ];
      } else if (financeCase === "Existing Practice") {
        options = [
          ...this.range(110_000, 500_000, 10_000),
          ...this.range(500_000, 2_000_000, 25_000),
          ...this.range(2_000_000, 5_000_000, 100_000),
        ];
      }
      else if (financeCase === "Debt Restructuring" || financeCase === 'Equipment') {
        options = [
          ...this.range(20_000, 50_000, 2_500),
          ...this.range(50_000, 100_000, 5_000),
          ...this.range(100_000, 500_000, 10_000),
          ...this.range(500_000, 2_000_000, 25_000),
          ...this.range(2_000_000, 5_100_000, 100_000),
        ];
      } else if (financeCase === "Liquidity") {
        options = [
          ...this.range(20_000, 50_000, 2_500),
          ...this.range(50_000, 105_000, 5_000)
        ];
      } else {
        options = [];
      }
    }

    return options;
  }

  range(start, end, step) {
    let result = [];
    for (let i = start; i < end; i += step) {
      result.push(i);
    }
    return result;
  }

  resetAmountAndDuration() {
    this.amountTarget.value = '';
    this.durationTarget.value = '';
    this.reevaluate();
  }

  reevaluateDuration() {
    const amount = this.amountTarget.value;
    const financeCase = this.financeCaseTarget.value;
    const currentDuration = this.durationTarget.value;

    const durationOptions = this.calculateDurationOptions();
    this.durationTarget.innerHTML = "";

    const nullOption = document.createElement("option");
    nullOption.value = "";
    nullOption.text = "Bitte wählen";
    this.durationTarget.appendChild(nullOption);

    durationOptions.forEach((option) => {
      const optionElement = document.createElement("option");
      optionElement.value = option;
      optionElement.text = option / 12;
      if (optionElement.value === currentDuration) {
        optionElement.selected = 'selected';
      }
      this.durationTarget.appendChild(optionElement);
    });

    if (amount === "" || amount === 0 || amount === null || financeCase === "" || financeCase === null) {
      this.durationContainerTarget.classList.add("is-hidden");
    } else {
      this.durationContainerTarget.classList.remove("is-hidden");
    }
  }

  calculateDurationOptions() {
    const amount = this.amountTarget.value;
    const financeCase = this.financeCaseTarget.value;

    if (amount === "" || amount === 0 || amount === null || financeCase === "" || financeCase === null) {
      return [];
    }

    let options = [];

    if (this.wantsInstantLoanValue) {
      options = [
        ...this.range(12, 72, 12),
      ];
    } else {
      if (financeCase === "New Practice" || financeCase === "Buy Practice" || financeCase === "Existing Practice") {
        options = [
          ...this.range(24, 192, 12),
        ];
      } else if (financeCase === "Debt Restructuring") {
        if (amount <= 100_000) {
          options = [
            ...this.range(12, 72, 12),
          ];
        } else {
          options = [
            ...this.range(12, 132, 12),
          ];
        }
      } else if (financeCase === 'Equipment') {
        if (amount <= 100_000) {
          options = [
            ...this.range(12, 72, 12),
          ];
        } else {
          options = [
            ...this.range(12, 132, 12),
          ];
        }
      } else if (financeCase === "Liquidity") {
        options = [
          ...this.range(12, 72, 12)
        ];
      } else {
        options = [];
      }
    }
    return options;
  }
}
